/**
 * 시급×시간 산출액(기본급·연장·야간·휴일) 1의자리 절삭
 *
 * [스토리]
 * 시급에 1원 단위가 있으면 시급×시간(반올림) 때문에 기본급·연장·야간·휴일 수당에
 * 1원짜리가 생긴다. 그래서 반올림 뒤 1의자리(10원 미만)를 절삭한다.
 *
 * 다만 2026년 8월 이하 기간은 임금신고가 이미 끝난 상태다.
 * 그 달 계산·저장·일괄재계산에 절삭을 넣으면 신고액이 바뀌어 혼란이 난다.
 * 기존 DB 행을 일괄 고치지도 않는다.
 *
 * 적용: 2026년 9월(202609)부터. 8월 이하는 예전처럼 원 단위 반올림만.
 */
export const WAGE_ONES_TRUNCATE_FROM_YM = 202609;

/** 반올림 후 1의자리(10원 미만) 절삭 — 연월 가드는 호출측에서 */
export function truncateHourlyDerivedOnes(amount: number): number {
  const rounded = Math.round(Number(amount) || 0);
  return Math.floor(rounded / 10) * 10;
}

export function parseWageYmParts(
  yearOrYm?: number | string | null,
  month?: number | null
): { year: number; month: number } | null {
  if (
    month != null &&
    yearOrYm != null &&
    typeof yearOrYm === 'number' &&
    yearOrYm < 10000
  ) {
    const year = Number(yearOrYm);
    const m = Number(month);
    if (year >= 1 && m >= 1 && m <= 12) return { year, month: m };
  }
  const digits = String(yearOrYm ?? '').replace(/\D/g, '');
  if (digits.length < 6) return null;
  const year = Number(digits.slice(0, 4));
  const m = Number(digits.slice(4, 6));
  if (!year || m < 1 || m > 12) return null;
  return { year, month: m };
}

/** true = 2026-09 이후 → 1의자리 절삭 */
export function shouldTruncateWageOnes(
  yearOrYm?: number | string | null,
  month?: number | null
): boolean {
  const ym = parseWageYmParts(yearOrYm, month);
  if (!ym) return false;
  return ym.year * 100 + ym.month >= WAGE_ONES_TRUNCATE_FROM_YM;
}

/** 시급×시간·저장액 공통. 2026-08 이하·연월 없으면 반올림만 */
export function truncateWageOnes(
  amount: number,
  yearOrYm?: number | string | null,
  month?: number | null
): number {
  const rounded = Math.round(Number(amount) || 0);
  if (!shouldTruncateWageOnes(yearOrYm, month)) return rounded;
  return truncateHourlyDerivedOnes(rounded);
}

export function truncateWagePremiumPair(
  tax: number | string | null | undefined,
  nontax: number | string | null | undefined,
  yearOrYm?: number | string | null,
  month?: number | null
): { tax: number; nontax: number } {
  const taxNum = Number(tax) || 0;
  const nontaxNum = Number(nontax) || 0;
  if (!shouldTruncateWageOnes(yearOrYm, month)) {
    return { tax: Math.round(taxNum), nontax: Math.round(nontaxNum) };
  }
  const total = truncateWageOnes(taxNum + nontaxNum, yearOrYm, month);
  let nextNontax = truncateWageOnes(nontaxNum, yearOrYm, month);
  if (nextNontax > total) nextNontax = total;
  return { tax: total - nextNontax, nontax: nextNontax };
}
